import { useCallback, useEffect, useMemo, useState } from 'react';
import { hoaService, violationService, workOrderService } from '../clients/ApiClients';
import { UserType } from '../constants/UserTypes';
import { useUser } from '../hooks/UserContext';
import {
  fetchAllNotifications,
  isCommunication,
  isStaffNotification,
} from '../notification/notificationMeta';
import {
  useSyncReady,
  useSyncedAllComments,
  useSyncedCommunications,
  useSyncedHoas,
  useSyncedHomes,
  useSyncedMemberPlacements,
  useSyncedStaffNotifications,
  useSyncedViolations,
  useSyncedWorkOrders,
} from '../sync/hooks';
import { useSyncedReservations } from '../sync/reservationHooks';
import { apiErrorMessage } from '../utils/apiErrors';
import { EMPTY_SOURCES, HomeSources } from './homeMeta';

export interface HomeSourcesState {
  sources: HomeSources;
  /** Read from the sync engine's copy, so it changes as the data does. */
  live: boolean;
  loading: boolean;
  /** What could not be fetched, without a copy; one message per kind. */
  errors: string[];
  retry: () => void;
}

const NO_FILTER = {};

/**
 * Everything Home shows (#670). With the sync engine's copy, every kind is
 * read from it and the page follows each change as it lands — nothing is
 * fetched. Without one (the engine is down, or still on its way after the
 * provider's wait), violations, work orders and notifications are fetched
 * once, as their own pages do; comments, reservations, homes and members
 * have no all-associations endpoint to fall back on and stay out until a
 * copy arrives.
 */
export function useHomeSources(): HomeSourcesState {
  const { userType, companyId } = useUser();
  // The board's copy leaves residents' mail out until #626 decides.
  const readsMail = userType !== UserType.BOARD_MEMBER;

  const ready = useSyncReady();
  const violations = useSyncedViolations(NO_FILTER);
  const workOrders = useSyncedWorkOrders(NO_FILTER);
  const comments = useSyncedAllComments();
  const notifications = useSyncedStaffNotifications();
  const communications = useSyncedCommunications(readsMail);
  const reservations = useSyncedReservations(NO_FILTER);
  const homes = useSyncedHomes(NO_FILTER);
  const members = useSyncedMemberPlacements();
  const hoas = useSyncedHoas();

  const synced = useMemo<HomeSources | null>(
    () =>
      ready
        ? {
            violations: violations ?? [],
            workOrders: workOrders ?? [],
            comments: comments ?? [],
            notifications: notifications ?? [],
            communications: communications ?? [],
            reservations: reservations ?? [],
            homes: homes ?? [],
            members: members ?? [],
            hoas: hoas ?? [],
          }
        : null,
    [
      ready,
      violations,
      workOrders,
      comments,
      notifications,
      communications,
      reservations,
      homes,
      members,
      hoas,
    ]
  );

  const [fetched, setFetched] = useState<HomeSources>(EMPTY_SOURCES);
  const [loading, setLoading] = useState(!ready);
  const [errors, setErrors] = useState<string[]>([]);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (ready) return;
    let live = true;
    setLoading(true);
    setErrors([]);
    // Independent: one failing leaves the others showing.
    const failed: string[] = [];
    const settle = <T,>(
      request: Promise<T>,
      fallback: string,
      apply: (value: T) => Partial<HomeSources>
    ) =>
      request.then(
        (value) => live && setFetched((prev) => ({ ...prev, ...apply(value) })),
        (err) => {
          failed.push(apiErrorMessage(err, fallback));
        }
      );
    Promise.all([
      settle(
        violationService.getAllViolationsForUser(),
        "Couldn't load violations.",
        (r) => ({ violations: r.data.violations })
      ),
      settle(
        workOrderService.getAllWorkOrdersForUser(),
        "Couldn't load work orders.",
        (r) => ({ workOrders: r.data.work_orders })
      ),
      settle(fetchAllNotifications(), "Couldn't load notifications.", (all) => ({
        notifications: all.filter(isStaffNotification),
        communications: readsMail ? all.filter(isCommunication) : [],
      })),
      // The sidebar's source. Only names and the money panel's list hang on
      // it, so a failure is quiet: rows show without an association.
      (userType === UserType.SUPER_ADMIN
        ? hoaService.getAllHoas()
        : companyId
          ? hoaService.getHoasByCompany({ companyId })
          : Promise.resolve(null)
      ).then(
        (r) =>
          live &&
          r &&
          setFetched((prev) => ({
            ...prev,
            hoas: r.data.filter((h) => h.is_active !== false),
          })),
        () => undefined
      ),
    ]).then(() => {
      if (!live) return;
      setErrors(failed);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [ready, readsMail, userType, companyId, attempt]);

  if (synced) {
    return { sources: synced, live: true, loading: false, errors: [], retry };
  }
  return { sources: fetched, live: false, loading, errors, retry };
}
